import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../lib/storage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS || 15000);

const api = axios.create({
  baseURL: API_URL,
  timeout: Number.isFinite(API_TIMEOUT_MS) && API_TIMEOUT_MS > 0 ? API_TIMEOUT_MS : 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = storage.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['X-Client-Action-Id'] = config.headers['X-Client-Action-Id'] || uuidv4();
    config.headers['X-Client-Surface'] = config.headers['X-Client-Surface'] || 'web-admin';

    if (typeof window !== 'undefined') {
      config.headers['X-Client-Route'] =
        config.headers['X-Client-Route'] || `${window.location.pathname}${window.location.search}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

const extractErrorMessage = (error) => {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. Please try again.';
    }
    return 'Network error. Please check your connection.';
  }

  const { data, status } = error.response;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors.map((detail) => detail.message).join('. ');
  }

  if (data?.error?.message) {
    return data.error.message;
  }

  if (data?.message) {
    return data.message;
  }

  const statusMessages = {
    400: 'Invalid request. Please check your input.',
    401: 'Unauthorized. Please login again.',
    403: 'Access denied.',
    404: 'Resource not found.',
    409: 'This record already exists.',
    410: 'This link has expired or has already been used.',
    422: 'Request could not be processed. Please check your input.',
    429: 'Too many requests. Please wait and try again.',
    500: 'Server error. Please try again later.',
    503: 'Service temporarily unavailable. Please try again later.',
  };

  return statusMessages[status] || 'An error occurred. Please try again.';
};

export const maybeDispatchOperationBlockEvent = (error) => {
  if (error.response?.status !== 503) {
    return false;
  }

  const code = error.response?.data?.error?.code;
  const details = error.response?.data?.error?.details;

  if (code !== 9101 || !details || typeof window === 'undefined') {
    return false;
  }

  window.dispatchEvent(
    new CustomEvent('parktek:operation-blocked', {
      detail: details,
    }),
  );
  return true;
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    error.userMessage = extractErrorMessage(error);

    maybeDispatchOperationBlockEvent(error);

    if (error.response?.status === 401) {
      const hadSessionToken = Boolean(storage.getToken());
      storage.clearSession();

      if (hadSessionToken) {
        window.dispatchEvent(new CustomEvent('parktek:unauthorized'));
      }
    }

    return Promise.reject(error);
  },
);

export default api;

export const getErrorMessage = (error) => {
  return error?.userMessage || error?.response?.data?.message || error?.message || 'An error occurred';
};

export const getFieldErrors = (error) => {
  const errors = error.response?.data?.errors;

  if (!errors || !Array.isArray(errors)) {
    return {};
  }

  return errors.reduce((accumulator, detail) => {
    if (detail.field) {
      accumulator[detail.field] = detail.message;
    }

    return accumulator;
  }, {});
};

export const MULTIPART_CONFIG = {
  headers: {
    'Content-Type': 'multipart/form-data',
  },
};
